import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Waves, Loader2 } from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { LANGUAGES } from "@/lib/data";
import { LanguageSelector } from "@/components/LanguageSelector";
import { AllergenBadges } from "@/components/AllergenBadges";

const ProductRow = ({ p }) => (
  <div
    data-testid={`product-${p.id}`}
    className={`flex gap-4 py-5 ${p.available === false ? "opacity-45" : ""}`}
  >
    {p.image ? (
      <img src={imgUrl(p.image)} alt={p.name} className="h-20 w-20 shrink-0 rounded-xl object-cover ring-1 ring-black/5" />
    ) : null}
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-[#1C1917]">{p.name}</h3>
        <div className="shrink-0 text-right">
          {(p.prices || []).map((pr, i) => (
            <div key={i} className="whitespace-nowrap text-sm">
              {pr.label ? <span className="mr-1.5 text-xs uppercase tracking-wide text-[#A8A29E]">{pr.label}</span> : null}
              <span className="font-bold text-[#0369A1]">{formatPrice(pr.amount)}</span>
            </div>
          ))}
        </div>
      </div>
      {p.description ? <p className="mt-1 text-sm leading-relaxed text-[#57534E]">{p.description}</p> : null}
      <div className="mt-2"><AllergenBadges allergens={p.allergens} /></div>
    </div>
  </div>
);

export default function MenuView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("salitre_lang") || "es");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [logo, setLogo] = useState("");
  const sectionRefs = useRef({});
  const tabRefs = useRef({});

  useEffect(() => { api.get("/settings").then((r) => setLogo(r.data.logo || "")).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const langName = (LANGUAGES.find((l) => l.code === lang) || {}).name;
    api.get(`/menu/${slug}`, { params: lang !== "es" ? { lang: langName } : {} })
      .then((r) => { setData(r.data); if (r.data.categories[0]) setActive(r.data.categories[0].id); })
      .finally(() => setLoading(false));
  }, [slug, lang]);

  const changeLang = (code) => { setLang(code); localStorage.setItem("salitre_lang", code); };

  // Scrollspy: activa la pestaña de la categoría visible al hacer scroll
  useEffect(() => {
    const cats = data?.categories || [];
    if (!cats.length) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible[0]) setActive(visible[0].target.dataset.catid);
    }, { rootMargin: "-120px 0px -65% 0px", threshold: [0, 0.2, 0.5, 1] });
    cats.forEach((c) => { const el = sectionRefs.current[c.id]; if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [data]);

  // Mantiene visible la pestaña activa en la barra horizontal
  useEffect(() => {
    if (active && tabRefs.current[active]) {
      tabRefs.current[active].scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [active]);

  const scrollTo = (id) => {
    setActive(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cats = data?.categories || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24">
      <header className="sticky top-0 z-40 border-b border-[#E7E5E4] bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <button data-testid="back-to-home" onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-[#57534E] transition-colors hover:bg-secondary">
            <ChevronLeft className="h-4 w-4" /> Inicio
          </button>
          <div className="flex items-center gap-1.5 text-[#075985]">
            {logo ? <img src={imgUrl(logo)} alt="" className="h-9 w-auto object-contain" /> : <Waves className="h-5 w-5" />}
            <span className="font-display text-lg font-bold">{data?.menu?.title || "Carta"}</span>
          </div>
          <LanguageSelector value={lang} onChange={changeLang} />
        </div>
        {cats.length > 0 && (
          <div className="no-scrollbar mx-auto flex max-w-3xl gap-2 overflow-x-auto px-4 pb-3">
            {cats.map((c) => (
              <button
                key={c.id}
                ref={(el) => (tabRefs.current[c.id] = el)}
                data-testid={`category-tab-${c.id}`}
                onClick={() => scrollTo(c.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  active === c.id ? "bg-[#0369A1] text-white" : "bg-secondary text-[#57534E] hover:bg-[#E0F2FE]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-[#0369A1]"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : cats.length === 0 ? (
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
          <p className="font-display text-2xl font-semibold text-[#1C1917]">Carta en preparación</p>
          <p className="mt-2 text-[#57534E]">Pronto encontrarás aquí nuestros platos.</p>
        </div>
      ) : (
        <main className="mx-auto max-w-3xl px-4">
          {cats.map((c, ci) => (
            <section key={c.id} data-catid={c.id} ref={(el) => (sectionRefs.current[c.id] = el)} className="scroll-mt-36 pt-10">
              <motion.h2
                initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                className="font-display text-2xl md:text-3xl font-bold text-[#1C1917]"
              >
                {c.name}
              </motion.h2>
              <div className="mt-1 h-1 w-14 rounded-full bg-[#EA580C]" />
              <div className="mt-2 divide-y divide-[#EDE8E0]">
                {c.products.map((p) => <ProductRow key={p.id} p={p} />)}
              </div>
            </section>
          ))}
          <div className="mt-16 rounded-2xl bg-[#E0F2FE] p-5 text-center text-sm text-[#075985]">
            Si tienes alguna alergia o intolerancia, consulta a nuestro personal. Los iconos indican los alérgenos de cada plato.
          </div>
        </main>
      )}
    </div>
  );
}
